import { useState, useCallback, useMemo } from 'react';
import {
  Stack,
  Group,
  TextInput,
  Textarea,
  Button,
  Text,
  Paper,
  Center,
  Loader,
  ScrollArea,
  Divider,
  TagsInput,
  ActionIcon,
} from '@mantine/core';
import { IconArrowLeft, IconPlus, IconTrash } from '@tabler/icons-react';
import { useSubscription } from 'urql';
import { graphql } from '../lib/graphql';
import { api } from '../lib/api';
import { getErrorMessage } from '../lib/error-message';
import { ErrorAlert } from './error-alert';

const PersonaTemplateForUseSubscription = graphql(`
  subscription PersonaTemplateForUse($templateId: Uuid!) {
    personaTemplatesByPk(id: $templateId) {
      id
      name
      description
      variables
      personaName
      personaDescription
      greeting
    }
  }
`);

const FlowTemplateForUseSubscription = graphql(`
  subscription FlowTemplateForUse($templateId: Uuid!) {
    flowTemplatesByPk(id: $templateId) {
      id
      name
      description
      variables
      flowName
      flowDescription
    }
  }
`);

type TemplateVariable = {
  key: string;
  label: string;
  helpText?: string;
  type: string;
  required: boolean;
  defaultValue?: string;
  isKnowledgeSource?: boolean;
};

type WizardTemplate = {
  name: string;
  description: string;
  variables: TemplateVariable[];
  previewName: string;
  previewDescription: string;
  greeting: string;
};

function isTemplateVariable(value: unknown): value is TemplateVariable {
  if (!value || typeof value !== 'object') return false;

  const variable = value as Partial<TemplateVariable>;

  return (
    typeof variable.key === 'string' &&
    typeof variable.label === 'string' &&
    typeof variable.type === 'string' &&
    typeof variable.required === 'boolean'
  );
}

function readVariables(value: unknown): TemplateVariable[] {
  return Array.isArray(value) ? value.filter(isTemplateVariable) : [];
}

type TemplateVariableWizardProps = {
  templateId: string;
  type: 'persona' | 'flow';
  organizationId: string;
  onComplete: (newId: string) => void;
  onBack: () => void;
};

export function TemplateVariableWizard({
  templateId,
  type,
  organizationId,
  onComplete,
  onBack,
}: TemplateVariableWizardProps) {
  const [{ data: personaData }] = useSubscription({
    query: PersonaTemplateForUseSubscription,
    variables: { templateId },
    pause: type !== 'persona',
  });

  const [{ data: flowData }] = useSubscription({
    query: FlowTemplateForUseSubscription,
    variables: { templateId },
    pause: type !== 'flow',
  });

  const template = useMemo<WizardTemplate | null | undefined>(() => {
    if (type === 'persona') {
      if (personaData === undefined) return undefined;

      const source = personaData.personaTemplatesByPk;

      if (!source) return null;

      return {
        name: source.name,
        description: source.description,
        variables: readVariables(source.variables),
        previewName: source.personaName,
        previewDescription: source.personaDescription,
        greeting: source.greeting,
      };
    }

    if (flowData === undefined) return undefined;

    const source = flowData.flowTemplatesByPk;

    if (!source) return null;

    return {
      name: source.name,
      description: source.description,
      variables: readVariables(source.variables),
      previewName: source.flowName,
      previewDescription: source.flowDescription ?? '',
      greeting: '',
    };
  }, [type, personaData, flowData]);

  const [filledVars, setFilledVars] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateVar = useCallback((key: string, value: string) => {
    setFilledVars((prev) => ({ ...prev, [key]: value }));
  }, []);

  const isValid = useMemo(() => {
    if (!template) return false;
    return template.variables.every((v) => !v.required || filledVars[v.key]?.trim() || v.defaultValue?.trim());
  }, [template, filledVars]);

  const handleCreate = useCallback(async () => {
    if (!template) return;
    setIsSubmitting(true);
    setError(null);

    const body = { organizationId, filledVariables: filledVars };
    const result =
      type === 'persona'
        ? await api['persona-templates']({ templateId }).use.post(body)
        : await api['flow-templates']({ templateId }).use.post(body);

    if (result.error) setError(getErrorMessage(result.error, 'Failed to create from template'));
    else onComplete(result.data.id);

    setIsSubmitting(false);
  }, [template, type, templateId, organizationId, filledVars, onComplete]);

  if (template === undefined) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!template) {
    return <ErrorAlert message="Template not found" />;
  }

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />} onClick={onBack}>
          Back
        </Button>
        <Text fw={600}>{template.name}</Text>
      </Group>

      <Text size="sm" c="dimmed">
        {template.description}
      </Text>

      <ErrorAlert message={error} />

      {template.variables.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          This template has no variables. Click Create to use it as-is.
        </Text>
      ) : (
        <ScrollArea.Autosize mah={400}>
          <Stack gap="sm">
            {template.variables.map((variable) => (
              <VariableInput
                key={variable.key}
                variable={variable}
                value={filledVars[variable.key] ?? ''}
                onChange={(val) => updateVar(variable.key, val)}
              />
            ))}
          </Stack>
        </ScrollArea.Autosize>
      )}

      <Divider />

      <PreviewSection template={template} type={type} filledVars={filledVars} />

      <Group justify="flex-end">
        <Button variant="subtle" onClick={onBack}>
          Cancel
        </Button>
        <Button onClick={handleCreate} loading={isSubmitting} disabled={!isValid}>
          Create {type === 'persona' ? 'Persona' : 'Flow'}
        </Button>
      </Group>
    </Stack>
  );
}

type VariableInputProps = {
  variable: TemplateVariable;
  value: string;
  onChange: (value: string) => void;
};

function VariableInput({ variable, value, onChange }: VariableInputProps) {
  const knowledgeHint = variable.isKnowledgeSource ? 'Will be added to the knowledge base' : undefined;

  switch (variable.type) {
    case 'text':
      return (
        <Textarea
          label={variable.label}
          description={knowledgeHint || variable.helpText}
          placeholder={variable.defaultValue || `Enter ${variable.label.toLowerCase()}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={variable.required}
          autosize
          minRows={2}
        />
      );
    case 'url':
      return (
        <TextInput
          label={variable.label}
          description={knowledgeHint || variable.helpText}
          placeholder={variable.defaultValue || 'https://'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={variable.required}
          type="url"
        />
      );
    case 'phone_number':
      return (
        <TextInput
          label={variable.label}
          description={variable.helpText || 'Enter phone number with country code'}
          placeholder={variable.defaultValue || '+1 (555) 123-4567'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={variable.required}
        />
      );
    case 'list':
      return (
        <TagsInput
          label={variable.label}
          description={variable.helpText || 'Press Enter to add items'}
          placeholder={variable.defaultValue || 'Type and press Enter'}
          value={
            value
              ? value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              : []
          }
          onChange={(tags) => onChange(tags.join(', '))}
          required={variable.required}
        />
      );
    case 'url_list':
      return (
        <UrlListInput
          label={variable.label}
          helpText={variable.helpText}
          value={value}
          onChange={onChange}
          required={variable.required}
        />
      );
    case 'file':
    case 'file_list':
      return (
        <TextInput
          label={variable.label}
          description={knowledgeHint || variable.helpText || 'Enter file URL'}
          placeholder={variable.defaultValue || 'https://example.com/file.pdf'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={variable.required}
        />
      );
    default:
      return (
        <TextInput
          label={variable.label}
          description={variable.helpText}
          placeholder={variable.defaultValue || `Enter ${variable.label.toLowerCase()}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={variable.required}
        />
      );
  }
}

type UrlListInputProps = {
  label: string;
  helpText?: string;
  value: string;
  onChange: (value: string) => void;
  required: boolean;
};

function UrlListInput({ label, helpText, value, onChange, required }: UrlListInputProps) {
  const urls = value ? value.split('\n').filter(Boolean) : [''];

  const updateUrl = (index: number, url: string) => {
    const next = [...urls];
    next[index] = url;
    onChange(next.filter(Boolean).join('\n'));
  };

  const addUrl = () => {
    onChange([...urls.filter(Boolean), ''].join('\n'));
  };

  const removeUrl = (index: number) => {
    onChange(
      urls
        .filter((_, i) => i !== index)
        .filter(Boolean)
        .join('\n'),
    );
  };

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        {label}
        {required && <span style={{ color: 'var(--mantine-color-red-filled)' }}> *</span>}
      </Text>
      {helpText && (
        <Text size="xs" c="dimmed">
          {helpText}
        </Text>
      )}
      {urls.map((url, index) => (
        <Group key={index} gap="xs">
          <TextInput
            placeholder="https://"
            value={url}
            onChange={(e) => updateUrl(index, e.target.value)}
            style={{ flex: 1 }}
            size="xs"
          />
          {urls.length > 1 && (
            <ActionIcon variant="subtle" color="red" size="sm" onClick={() => removeUrl(index)}>
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Group>
      ))}
      <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addUrl} w="fit-content">
        Add URL
      </Button>
    </Stack>
  );
}

type PreviewSectionProps = {
  template: WizardTemplate;
  type: 'persona' | 'flow';
  filledVars: Record<string, string>;
};

function PreviewSection({ template, type, filledVars }: PreviewSectionProps) {
  const vars = useMemo(() => {
    const result: Record<string, string> = {};
    for (const v of template.variables) {
      result[v.key] = filledVars[v.key]?.trim() || v.defaultValue || `<<${v.key}>>`;
    }
    return result;
  }, [template.variables, filledVars]);

  const substitute = (text: string) => text.replace(/<<(\w+)>>/g, (match, key: string) => vars[key] ?? match);

  return (
    <Paper p="sm" withBorder bg="gray.0">
      <Stack gap="xs">
        <Text size="xs" fw={600} tt="uppercase" c="dimmed">
          Preview
        </Text>
        <Group gap="xs">
          <Text size="xs" fw={500}>
            Name:
          </Text>
          <Text size="xs">{substitute(template.previewName)}</Text>
        </Group>
        {!!template.previewDescription && (
          <Group gap="xs">
            <Text size="xs" fw={500}>
              Description:
            </Text>
            <Text size="xs">{substitute(template.previewDescription)}</Text>
          </Group>
        )}
        {type === 'persona' && !!template.greeting && (
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Text size="xs" fw={500}>
              Greeting:
            </Text>
            <Text size="xs">{substitute(template.greeting)}</Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
